import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash';
import React, { createContext, useContext } from 'react';
import { type FieldPath } from 'react-hook-form';
import { useKbUsageSocket } from '@/Domain/socket/Subscription/useKbUsageSocket';
import { arraysToObjects } from '@/Domain/utils/arrayToObject';

interface SubscriptionQuery {
  can_increase_messages: boolean;
  iframe: boolean;
  integrations: Record<string, boolean>;
  languages: number;
  campaigns: boolean;
  personalities: 'small';
  tier: 'free' | 'pro' | 'enterprise';
  user_roles: boolean;
  white_label: boolean;
  free_messages: number;
  can_delete: boolean;
  interval: 'month' | 'year';
  collaborators: number;
  human_handoff: {
    active: boolean;
    working_hours: boolean;
    keywords: boolean;
    custom_messages: boolean;
    ai_summary: boolean;
    email_notifications: boolean;
  };
  conversation_outcome: boolean;
  conversation_rating: boolean;
  lead_generation: boolean;
  export_conversations: boolean;
  custom_translations: number;
  professions: Record<string, boolean>;
  articles_limit: number | null;
  articles_used: number;
  ai_preferences: boolean;
  widget_customizable: boolean;
  can_add_article: boolean;
  can_add_article_intercom: boolean;
  ai_guidelines: boolean;
  can_add_bot: boolean;
  topics: boolean;
  allowed_integrations: Record<string, boolean>;
  widget_prechat_survey: boolean;
  new_message_sources_active: boolean;
}

type SubscriptionQueryPaths = FieldPath<SubscriptionQuery>;

export interface SubscriptionContextType {
  isActivated: (feature: SubscriptionQueryPaths) => boolean;
  getConfig: (config: SubscriptionQueryPaths) => unknown;
  isFetched: boolean;
}

export const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === null) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export function SubscriptionProvider({ children }: React.PropsWithChildren): JSX.Element {
  const { data = {}, isFetched } = useQuery<SubscriptionQuery>({
    queryKey: ['subscription'],
    select: (data) => arraysToObjects(data),
  });
  // Live-update articles_used / can_add_article on knowledge_base_updated by
  // writing the event payload directly into the cache (no refetch).
  useKbUsageSocket();

  const isActivated = (feature: SubscriptionQueryPaths): boolean => !!get(data, feature);
  const getConfig = (config: SubscriptionQueryPaths): unknown => get(data, config);

  const value = { isActivated, getConfig, isFetched };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
